import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { IconDotsVertical, IconUser } from "@tabler/icons-react";
import moment from "moment/moment";
import ProgressBar from "../UI/ProgressBar";
import styles from "./PropertyCard.module.css";
import { ROOT_PATH } from "./PropertyListShow";
// @ts-expect-error pure js
import Photo from "../UI/Photo";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import { PropertyStatus } from "../../models/property.js";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
import CountBadge from "../UI/CountBadge";
// @ts-expect-error pure js
import { getEffectivePropertyLoyerBreakdown } from "../../utils/properties";
import PropertyMoveToFolderDialog from "./PropertyMoveToFolderDialog";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import DropdownMenu from "../UI/DropdownMenu";
import Status from "../UI/Status.tsx";
import Text from "../UI/Text.tsx";
import Title from "../UI/Title.tsx";
import Card from "../UI/Card.tsx";
import ModalDialog from "../UI/ModalDialog.tsx";
import Button from "../UI/Button.tsx";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import type { Profile, ProfileBailleur } from "../Profile/types";

function profileDisplayName(profile: Profile): string {
  if (String(profile.type) === "2" && profile.nom_societe) {
    return profile.nom_societe;
  }
  return [profile.prenom, profile.nom].filter(Boolean).join(" ");
}

interface PropertyCardProps {
  property?: Property;
  propertyFolder?: PropertyFolder;
  onDuplicateProperty: (property: Property) => Promise<void>;
}

function PropertyPhoto({
  mainPhotoId,
  isFolder,
  type,
  isSubFolder,
}: {
  mainPhotoId?: number;
  isFolder?: boolean;
  type?: string;
  isSubFolder?: boolean;
}) {
  if (mainPhotoId) {
    return (
      <div className={styles.photoArea}>
        <Photo
          photoId={mainPhotoId}
          type="property_photo"
          alt="bien"
          cover
          style={{ height: "100px", borderRadius: 0, border: 0 }}
        />
      </div>
    );
  }

  let defaultImg = "/default-photo-appartement.jpeg";
  if (isFolder) {
    defaultImg = isSubFolder
      ? "/default-photo-appartement.jpeg"
      : "/default-photo-immeuble.jpeg";
  } else if (type === "commercial") {
    defaultImg = "/default-photo-commercial.jpeg";
  } else if (type === "garage") {
    defaultImg = "/default_garage.jpg";
  }

  return (
    <div className={styles.photoArea}>
      <img src={defaultImg} alt="bien" />
    </div>
  );
}

function FolderStatusBadge({ folder }: { folder: PropertyFolder }) {
  const properties = folder.properties || [];
  const { tenants } = useTenants() as { tenants: Tenant[] };

  const totalProperties = properties.length;
  if (totalProperties === 0) return null;

  const louees = properties.filter((prop) =>
    tenants.some(
      (ten) =>
        ten.id_bien === prop.id &&
        ten.status === 1 &&
        moment().isBetween(ten.debut, ten.fin),
    ),
  ).length;

  return (
    <Status
      color="success"
      text={
        <div>
          <div>
            <Text as="span" variant="deci" weight="bold">
              {louees}
            </Text>
            <Text as="span" variant="deci">
              /{totalProperties} loués
            </Text>
          </div>
          <ProgressBar value={louees} max={totalProperties} color="success" />
        </div>
      }
    />
  );
}

function PropertyStatusBadge({ property }: { property: Property }) {
  const { tenants } = useTenants() as { tenants: Tenant[] };

  const locataireActuel = tenants.find(
    (ten) =>
      ten.id_bien === property.id &&
      ten.status === 1 &&
      moment().isBetween(ten.debut, ten.fin),
  );

  const preavis = tenants.find(
    (ten) =>
      ten.id_bien === property.id &&
      ten.status === 1 &&
      ten.preavis &&
      moment().isBetween(ten.debut, ten.fin),
  );

  const nextLocataire = tenants.find(
    (ten) =>
      ten.id_bien === property.id &&
      ten.status === 1 &&
      moment().isBefore(ten.debut),
  );

  return (
    <>
      {!locataireActuel && <Status color="danger" text="Vide" />}
      {locataireActuel && preavis && <Status color="warning" text="Préavis" />}
      {locataireActuel && !preavis && <Status color="success" text="Loué" />}
      {nextLocataire && <Status color="violet" text="Reloué" />}
    </>
  );
}

export default function PropertyCard({
  property,
  propertyFolder,
  onDuplicateProperty,
}: PropertyCardProps) {
  const nav = useNavigate();
  const { updateProperty, deleteProperty } = useProperties() as {
    updateProperty: (
      id: number,
      data: Record<string, unknown>,
    ) => Promise<void>;
    deleteProperty: (id: number) => Promise<void>;
  };
  const { updatePropertyFolder, deletePropertyFolder } =
    usePropertyFolders() as {
      updatePropertyFolder: (
        id: number,
        data: Record<string, unknown>,
      ) => Promise<void>;
      deletePropertyFolder: (id: number) => Promise<void>;
    };
  const { profileBailleursById } = useProfileBailleurs({ withById: true }) as {
    profileBailleursById: Record<number, ProfileBailleur>;
  };
  const { tenants } = useTenants() as { tenants: Tenant[] };
  const [isMoveFolderOpen, setIsMoveFolderOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isFolder = !!propertyFolder;
  const item = propertyFolder || property;
  if (!item) return null;

  const profileOwner = profileBailleursById?.[item.id_profile_nom_owner];
  const ownerName = profileOwner?.nom_profile || "";

  const locataireActuel = !isFolder
    ? tenants.find(
        (ten) =>
          ten.id_bien === property!.id &&
          ten.status === 1 &&
          moment().isBetween(ten.debut, ten.fin),
      )
    : null;

  const loyer = (isFolder ? propertyFolder!.properties || [] : [property!])
    .map((p: Property) => getEffectivePropertyLoyerBreakdown(p, tenants))
    .reduce(
      (acc, b) => ({
        hc: acc.hc + b.hc,
        charges: acc.charges + b.charges,
        total: acc.total + b.total,
      }),
      { hc: 0, charges: 0, total: 0 },
    );

  const subtitle = isFolder
    ? `${(propertyFolder!.properties || []).length} Bien${
        (propertyFolder!.properties || []).length !== 1 ? "s" : ""
      }`
    : property!.rue && property!.ville
    ? `${property!.rue}, ${property!.ville}`
    : property!.ville || "";

  const activeProfiles = locataireActuel
    ? locataireActuel.profiles.filter(
        (profile) => profile.status === ProfileStatus.Actif.status,
      )
    : [];
  const tenantName =
    activeProfiles.length > 0
      ? profileDisplayName(activeProfiles[0])
      : locataireActuel?.nom;
  const extraTenantsCount = Math.max(0, activeProfiles.length - 1);

  const leaseDate =
    !isFolder && locataireActuel
      ? `${moment(locataireActuel.debut).format("D MMM YYYY")} - ${moment(
          locataireActuel.fin,
        ).format("D MMM YYYY")}`
      : null;

  const typeLabels: Record<string, string> = {
    habitation: "Habitation",
    commercial: "Commercial",
    garage: "Garage",
  };
  let typeBien: string | null = null;
  if (!isFolder && property!.type) {
    typeBien = property!.is_coloc
      ? "Colocation"
      : typeLabels[property!.type] || null;
  }

  const handleClick = () => {
    if (isFolder) {
      nav(`${ROOT_PATH}folder/${propertyFolder!.id}`);
    } else {
      nav(`${ROOT_PATH}${property!.id}`);
    }
  };

  const toggleArchived = async () => {
    const newStatus =
      item.status === PropertyStatus.Actif.status
        ? PropertyStatus.Archive.status
        : PropertyStatus.Actif.status;
    if (isFolder) {
      await updatePropertyFolder(item.id, { status: newStatus });
    } else {
      await updateProperty(item.id, { status: newStatus });
    }
  };

  const archiveLabel =
    item.status === PropertyStatus.Actif.status ? "Archiver" : "Réactiver";

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      if (isFolder) {
        await deletePropertyFolder(item.id);
      } else {
        await deleteProperty(item.id);
      }
      setIsDeleteConfirmOpen(false);
    } catch (e) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        "La suppression a échoué. Vérifiez qu'aucun élément lié n'empêche la suppression.";
      setDeleteError(message);
    }
  };

  return (
    <>
      <PropertyMoveToFolderDialog
        open={isMoveFolderOpen}
        onOpenChange={setIsMoveFolderOpen}
        target={{
          id: item.id,
          type: isFolder ? "folder" : "property",
          currentFolderId:
            (isFolder
              ? propertyFolder!.id_parent
              : property!.id_property_folder) ?? null,
        }}
        onMove={async (folderId) => {
          if (isFolder) {
            await updatePropertyFolder(item.id, { id_parent: folderId });
          } else {
            await updateProperty(item.id, { id_property_folder: folderId });
          }
        }}
      />
      <ModalDialog
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        header={isFolder ? "Supprimer ce dossier" : "Supprimer ce bien"}
        size="sm"
      >
        <Text className={"marginB10"}>
          Êtes-vous sûr de vouloir supprimer <strong>« {item.nom} »</strong> ?
          Cette action est irréversible et entraînera la suppression définitive
          de toutes les données associées (locataires, balances, documents,
          etc.).
        </Text>
        {deleteError && (
          <Text color="danger" className={"marginB10"}>
            {deleteError}
          </Text>
        )}
        <div className={"text-right"}>
          <Button
            variant="secondary"
            onClick={() => setIsDeleteConfirmOpen(false)}
          >
            Annuler
          </Button>
          <Button
            className={"marginL10"}
            variant="primary"
            onClick={() => {
              handleDelete().catch(console.error);
            }}
          >
            Supprimer
          </Button>
        </div>
      </ModalDialog>
      <Card padding="none" onClick={handleClick} className={styles.card}>
        <div className={styles.ownerRow}>
          <Text as="span" color="muted">
            {ownerName}
          </Text>
          <DropdownMenu icon={IconDotsVertical} label="Actions">
            <DropdownMenu.Item label="Gérer" onClick={handleClick} />
            {!isFolder && (
              <DropdownMenu.Item
                label="Dupliquer"
                requiresWrite
                profileId={property!.id_profile_nom_owner}
                onClick={() =>
                  onDuplicateProperty(property!).catch(console.error)
                }
              />
            )}
            <DropdownMenu.Item
              label="Déplacer dans un dossier"
              requiresWrite
              profileId={item.id_profile_nom_owner}
              onClick={() => setIsMoveFolderOpen(true)}
            />
            <DropdownMenu.Item
              label={archiveLabel}
              variant={
                item.status === PropertyStatus.Actif.status
                  ? "warning"
                  : "neutral"
              }
              requiresWrite
              profileId={item.id_profile_nom_owner}
              onClick={() => {
                toggleArchived().catch(console.error);
              }}
            />
            {item.status === PropertyStatus.Archive.status && (
              <DropdownMenu.Item
                label="Supprimer"
                variant="danger"
                requiresWrite
                profileId={item.id_profile_nom_owner}
                onClick={() => {
                  setDeleteError(null);
                  setIsDeleteConfirmOpen(true);
                }}
              />
            )}
          </DropdownMenu>
        </div>
        <div className={styles.photoWrapper}>
          <PropertyPhoto
            mainPhotoId={
              property?.main_photo_id || propertyFolder?.main_photo_id
            }
            isFolder={isFolder}
            type={property?.type}
            isSubFolder={!!propertyFolder?.id_parent}
          />
          <div className={styles.statusBadgeGroup}>
            {isFolder ? (
              <FolderStatusBadge folder={propertyFolder!} />
            ) : (
              <PropertyStatusBadge property={property!} />
            )}
            {typeBien && <Status color="info" text={typeBien} />}
          </div>
        </div>

        <div className={styles.titleRow}>
          <div>
            <Title
              as="div"
              variant="deca"
              weight="bold"
              className={styles.propertyName}
            >
              {item.nom}
            </Title>
            <Text variant="deci" color="muted">
              {subtitle}
            </Text>
          </div>
        </div>

        {locataireActuel && tenantName && (
          <div className={styles.tenantRow}>
            <IconUser size={16} stroke={1.5} className={styles.tenantIcon} />
            <Text as="span" variant="deci" className={styles.tenantName}>
              {tenantName}
            </Text>
            {extraTenantsCount > 0 && (
              <CountBadge count={`+${extraTenantsCount}`} />
            )}
          </div>
        )}

        <div className={styles.dateRow}>
          {leaseDate && (
            <Text as="span" variant="deci" color="muted">
              {leaseDate}
            </Text>
          )}
        </div>

        <div className={styles.priceRow}>
          <Text as="span" variant="deci" color="muted">
            Loyer
          </Text>
          <Text as="span" variant="base" weight="bold">
            {currencyFormatter(loyer.total)}
          </Text>
        </div>
        {loyer.charges > 0 && (
          <Text variant="deci" className={styles.priceBreakdown}>
            {currencyFormatter(loyer.hc)} HC +{" "}
            {currencyFormatter(loyer.charges)} de charges
          </Text>
        )}
      </Card>
    </>
  );
}
